import { router } from '@config/routing';
import environment from '@config/environment';
import { RouterProvider } from 'react-router-dom';
import useInitialization from '@hooks/useInitialization';
import { StatsigProvider } from '@statsig/react-bindings';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect } from 'react';
import { StatsigUserSync } from '@/components/StatsigUserSync';
import { FullStorySync } from '@/components/FullStorySync';
import { Toaster } from '@/components/design-system/toaster';
import { ErrorBoundaryWrapper } from '@/components/ErrorBoundary/ErrorBoundaryWrapper';
import { getErrorStatus } from '@/utils/sentryLogger';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      retry: (failureCount, error) => {
        const status = getErrorStatus(error);
        if (status && status !== 429 && status >= 400 && status < 500) return false;
        return failureCount < 3;
      },
    },
  },
});

function App() {
  useInitialization();

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://cdn.storifyme.xyz/static/web-components/storifyme-elements.min.js";
    script.defer = true;
    script.async = true;
    script.type = "text/javascript";
    document.head.appendChild(script);
  }, []);

  return (
    <ErrorBoundaryWrapper>
      <QueryClientProvider client={queryClient}>
        <StatsigProvider
          sdkKey={environment.STATSIG_API_KEY}
          user={{ userID: 'anonymous' }}
          options={{
            environment: { tier: environment.STATSIG_ENVIRONMENT },
          }}
        >
          <StatsigUserSync>
            <FullStorySync>
              <RouterProvider router={router} />
              <Toaster />
            </FullStorySync>
          </StatsigUserSync>
        </StatsigProvider>
        <ReactQueryDevtools />
      </QueryClientProvider>
    </ErrorBoundaryWrapper>
  );
}

export default App;
